import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bug, FileSpreadsheet, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import {
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  feedbackApi,
  type ErrorReportOut,
  type ReportCategory,
  type ReportPriority,
  type ReportStatus,
} from "@/services/feedback-api.service";

function statusBadgeVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  if (status === "new") return "default";
  if (status === "in_progress") return "secondary";
  if (status === "resolved") return "outline";
  return "outline";
}

export default function FeedbackAdmin() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("all");
  const [priority, setPriority] = useState<string>("all");
  const [category, setCategory] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number[]>([]);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [exporting, setExporting] = useState(false);

  const list = useQuery({
    queryKey: ["admin-feedback", status, priority, category, page],
    queryFn: () =>
      feedbackApi.adminList({
        status: status === "all" ? undefined : status,
        priority: priority === "all" ? undefined : priority,
        category: category === "all" ? undefined : category,
        page,
        page_size: 20,
      }),
  });

  const detail = useQuery({
    queryKey: ["admin-feedback-detail", detailId],
    queryFn: () => feedbackApi.adminGet(detailId!),
    enabled: detailId != null,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: {
      id: number;
      status?: ReportStatus;
      priority?: ReportPriority;
    }) => feedbackApi.adminUpdate(payload.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
      qc.invalidateQueries({ queryKey: ["admin-feedback-detail"] });
      toast({ title: "Сохранено" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Ошибка",
        description: err instanceof Error ? err.message : "Не удалось обновить",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => feedbackApi.adminDelete(id),
    onSuccess: () => {
      setDeleteId(null);
      setDetailId(null);
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
      toast({ title: "Отчёт удалён" });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (ids: number[]) => feedbackApi.adminBulkDelete(ids),
    onSuccess: (res) => {
      setBulkOpen(false);
      setSelected([]);
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
      toast({ title: "Удалено", description: `Записей: ${res.deleted}` });
    },
  });

  const commentMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      feedbackApi.adminAddComment(id, body),
    onSuccess: () => {
      setComment("");
      qc.invalidateQueries({ queryKey: ["admin-feedback-detail"] });
      toast({ title: "Комментарий добавлен" });
    },
  });

  const items = list.data?.items ?? [];
  const totalPages = useMemo(() => {
    const total = list.data?.total ?? 0;
    const size = list.data?.page_size ?? 20;
    return Math.max(1, Math.ceil(total / size));
  }, [list.data]);

  const toggleSelect = (id: number, checked: boolean) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handleExportXlsx = async () => {
    setExporting(true);
    try {
      await feedbackApi.adminExportXlsx({
        status: status === "all" ? undefined : status,
        priority: priority === "all" ? undefined : priority,
        category: category === "all" ? undefined : category,
      });
      toast({ title: "Файл скачан" });
    } catch (err: unknown) {
      toast({
        title: "Не удалось выгрузить",
        description: err instanceof Error ? err.message : "Ошибка экспорта",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const report: ErrorReportOut | undefined = detail.data;
  const metadataEntries = Object.entries(report?.metadata ?? {});

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Bug className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Обратная связь</h2>
        <SectionHelpTooltip label="Справка по обратной связи">
          <p className="font-medium">Зачем</p>
          <p>Очередь отчётов об ошибках от пользователей.</p>
          <p className="mt-2 font-medium">Как работать</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Фильтры по статусу, приоритету и категории.</li>
            <li>Клик по строке — детали, metadata (в т.ч. IP), вложения.</li>
            <li>«Выгрузить в Excel» — все записи по текущим фильтрам (лимит на сервере).</li>
            <li>Удаление — только через диалог подтверждения.</li>
          </ul>
        </SectionHelpTooltip>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label>Статус</Label>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="all">Все</SelectItem>
              {(Object.keys(STATUS_LABELS) as ReportStatus[]).map((code) => (
                <SelectItem key={code} value={code}>
                  {STATUS_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Приоритет</Label>
          <Select value={priority} onValueChange={(v) => { setPriority(v); setPage(1); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="all">Все</SelectItem>
              {(Object.keys(PRIORITY_LABELS) as ReportPriority[]).map((code) => (
                <SelectItem key={code} value={code}>
                  {PRIORITY_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Категория</Label>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="all">Все</SelectItem>
              {(Object.keys(CATEGORY_LABELS) as ReportCategory[]).map((code) => (
                <SelectItem key={code} value={code}>
                  {CATEGORY_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={exporting || list.isLoading}
          onClick={() => void handleExportXlsx()}
        >
          <FileSpreadsheet className="mr-1.5 h-4 w-4" />
          {exporting ? "Выгрузка…" : "Выгрузить в Excel"}
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={selected.length === 0}
          onClick={() => setBulkOpen(true)}
        >
          <Trash2 className="mr-1.5 h-4 w-4" />
          Удалить выбранные ({selected.length})
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-2 w-10" />
              <th className="p-2">ID</th>
              <th className="p-2">Заголовок</th>
              <th className="p-2">Категория</th>
              <th className="p-2">Статус</th>
              <th className="p-2">Приоритет</th>
              <th className="p-2">Создан</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.id}
                className="border-t cursor-pointer hover:bg-muted/30"
                onClick={() => setDetailId(row.id)}
              >
                <td className="p-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.includes(row.id)}
                    onCheckedChange={(c) => toggleSelect(row.id, c === true)}
                  />
                </td>
                <td className="p-2 font-mono text-xs">{row.id}</td>
                <td className="p-2 max-w-[240px] truncate">{row.title}</td>
                <td className="p-2">
                  {CATEGORY_LABELS[row.category as ReportCategory] ?? row.category}
                </td>
                <td className="p-2">
                  <Badge variant={statusBadgeVariant(row.status)}>
                    {STATUS_LABELS[row.status as ReportStatus] ?? row.status}
                  </Badge>
                </td>
                <td className="p-2">
                  {PRIORITY_LABELS[row.priority as ReportPriority] ?? row.priority}
                </td>
                <td className="p-2 whitespace-nowrap text-xs text-muted-foreground">
                  {new Date(row.created_at).toLocaleString("ru-RU")}
                </td>
              </tr>
            ))}
            {items.length === 0 && !list.isLoading && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-muted-foreground">
                  Отчётов нет
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Всего: {list.data?.total ?? 0}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Назад
          </Button>
          <span className="text-sm self-center">
            {page} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Вперёд
          </Button>
        </div>
      </div>

      <Dialog open={detailId != null} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent className="z-[230] max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Отчёт #{detailId}</DialogTitle>
          </DialogHeader>
          {report && (
            <div className="space-y-4 text-sm">
              <div>
                <div className="font-medium text-base">{report.title}</div>
                <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                  {report.description}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Статус</Label>
                  <Select
                    value={report.status}
                    onValueChange={(v) =>
                      updateMutation.mutate({ id: report.id, status: v as ReportStatus })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[250]">
                      {(Object.keys(STATUS_LABELS) as ReportStatus[]).map((code) => (
                        <SelectItem key={code} value={code}>
                          {STATUS_LABELS[code]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Приоритет</Label>
                  <Select
                    value={report.priority}
                    onValueChange={(v) =>
                      updateMutation.mutate({ id: report.id, priority: v as ReportPriority })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[250]">
                      {(Object.keys(PRIORITY_LABELS) as ReportPriority[]).map((code) => (
                        <SelectItem key={code} value={code}>
                          {PRIORITY_LABELS[code]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <h4 className="mb-2 font-medium">Метаданные</h4>
                {metadataEntries.length === 0 ? (
                  <p className="text-muted-foreground">Нет данных</p>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {metadataEntries.map(([k, v]) => (
                        <tr key={k} className="border-t">
                          <td className="py-1 pr-3 font-mono text-muted-foreground">{k}</td>
                          <td className="py-1 break-all">{String(v ?? "")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div>
                <h4 className="mb-2 font-medium">Вложения</h4>
                {(report.attachments ?? []).length === 0 ? (
                  <p className="text-muted-foreground">Нет вложений</p>
                ) : (
                  <ul className="space-y-1">
                    {report.attachments.map((att) => (
                      <li key={att.id}>
                        <a
                          className="text-primary underline-offset-2 hover:underline"
                          href={att.download_url || undefined}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {att.file_name} ({Math.round(att.file_size / 1024)} КБ)
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="mb-2 font-medium">Комментарии</h4>
                <ul className="mb-2 space-y-2">
                  {(report.comments ?? []).map((c) => (
                    <li key={c.id} className="rounded border p-2">
                      <div className="text-xs text-muted-foreground">
                        {new Date(c.created_at).toLocaleString("ru-RU")}
                      </div>
                      <div className="whitespace-pre-wrap">{c.body}</div>
                    </li>
                  ))}
                </ul>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Новый комментарий"
                  rows={3}
                />
                <Button
                  type="button"
                  className="mt-2"
                  size="sm"
                  disabled={!comment.trim() || commentMutation.isPending}
                  onClick={() =>
                    commentMutation.mutate({ id: report.id, body: comment.trim() })
                  }
                >
                  Добавить комментарий
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setDeleteId(report?.id ?? null)}
            >
              Удалить
            </Button>
            <Button type="button" variant="outline" onClick={() => setDetailId(null)}>
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить выбранные отчёты?</AlertDialogTitle>
            <AlertDialogDescription>
              Будет удалено записей: {selected.length}. Вложения удалятся из хранилища.
              Действие нельзя отменить в UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkMutation.isPending}
              onClick={() => bulkMutation.mutate(selected)}
            >
              {bulkMutation.isPending ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteId != null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить отчёт #{deleteId}?</AlertDialogTitle>
            <AlertDialogDescription>
              Отчёт и вложения будут удалены безвозвратно в UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => deleteId != null && deleteMutation.mutate(deleteId)}
            >
              {deleteMutation.isPending ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
